// src/encounter/note-column/to-note-complaints.ts
//
// THE CHIEF COMPLAINT THE NOTE WAS MISSING.
//
// The note's "Chief Complaint" section rendered ONLY the HPI questionnaire answers, so on an
// encounter whose complaints were saved through the CC seam and whose HPI questionnaire was
// untouched, the note printed Flutter's "No CC data selected." — asserting an absence that the
// record contradicts. That is a note misrepresenting the chart, which is the worst class of bug on
// this surface.
//
// Flutter reads the complaints, not the questionnaire: `CCNoteComponentWidget.additionWidget`
// sources `complaints ?? bloc.state.newAppointment.ccData`
// (`chief_complaint_note_component_widget.dart.dart:52-53`) and draws
//   · an optional leading `Notes` row from `patientComplaints.comment` at `titleWidth: 104 - 26 = 78`
//     (`:64-78`);
//   · one row per complaint, key the LITERAL "Chief Complaint" at width 104, value
//     `"{title} ({complaintCategory})"` with the parenthetical only when the category is non-blank
//     (`:124-152`);
//   · complaints deduped on `(coding.dis, complaintCategory)` (`:54-60`).
//
// The PA reads the same record the HPI step does — `usePatientComplaint` -> GET
// `/api/Patients/GetPatientComplaint` — so this is a wiring fix, not a new data source.
import { deltaPlainText, parseNoteOps } from '../lib/note-delta';
import type { PatientComplaintModel } from '../lib/to-chief-complaint';
import type { GeneratedNoteComplaintRow, GeneratedNoteComplaints } from '@scripsteam/scrips-signal-ds';

/**
 * One rendered complaint row, and the complaints block — the Signal DS `GeneratedNote` shapes (the
 * organism draws them; `complaintRowValue` is the DS's too). The PA names are kept because the
 * drawer and the page import them.
 */
export type NoteComplaintRow = GeneratedNoteComplaintRow;
export type NoteComplaints = GeneratedNoteComplaints;

/**
 * The complaint rows + the leading Notes row, from the SAME `PatientComplaintModel` the HPI step
 * reads. Returns `{ rows: [] }` when the record genuinely holds no complaint, which is what lets
 * the caller keep Flutter's empty state honest.
 *
 * The comment is stored as a Quill Delta (the app's own note format — `clinical-note-dialog.tsx`
 * writes it, `lib/note-delta.ts` parses it), so it is flattened with the EXISTING helpers rather
 * than a second parser: `parseNoteOps` tolerates a non-Delta string by returning `[]`, in which
 * case `notePlainText` — the plain mirror the write path already persists — is used instead.
 */
export function toNoteComplaints(model: PatientComplaintModel | undefined): NoteComplaints {
  if (!model) return { rows: [] };

  const seen = new Set<string>();
  const rows: NoteComplaintRow[] = [];
  for (const entry of model.complaints ?? []) {
    const title = (entry.coding?.display ?? '').trim();
    if (!title) continue; // Flutter renders a blank-titled complaint as an 8px gap, not a row.
    const category = (entry.complaintCategory ?? '').trim();
    // Flutter's dedupe key is the pair, not the title — the same complaint under two categories is
    // two clinical statements.
    const key = `${title} ${category}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(category ? { title, category } : { title });
  }

  const fromDelta = deltaPlainText(parseNoteOps(model.comment)).trim();
  const note = fromDelta || (model.notePlainText ?? '').trim();

  return note ? { rows, note } : { rows };
}
